<template>
  <PageLoader />
  <AgeVerification v-if="!inMaintenancePage" />
  <NavbarComponent v-if="!inMaintenancePage" />
  <router-view />
  <FooterComponent v-if="!inMaintenancePage" />
</template>
<script setup>
import PageLoader from '@/components/PageLoader.vue'
import AgeVerification from '@/components/AgeVerification.vue'
import NavbarComponent from '@/components/NavbarComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import { onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const inMaintenancePage = computed(() => route.name === 'MaintenancePage');

// Iniciar el monitor de mantenimiento después de que la aplicación esté montada
// para evitar problemas de inicialización circular
onMounted(async () => {
  try {
    const { useMaintenanceMonitor } = await import('@/utils/maintenanceMonitor');
    useMaintenanceMonitor(3);
  } catch (error) {
    console.error('Error al iniciar el monitor de mantenimiento:', error);
  }
});

onMounted(() => {
  // Verificar si hay datos en caché del formato antiguo y limpiarlos
  const oldCacheKeys = [
    'products',
    'productsTimestamp',
    'userOrders',
    'adminOrders',
    'ordersTimestamp'
  ];

  const hasOldCache = oldCacheKeys.some(key => {
    const value = localStorage.getItem(key);
    try {
      if (value) JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  });

  if (hasOldCache && !localStorage.getItem('cacheVersion')) {
    console.log('Detectado caché antiguo, limpiando...');
    oldCacheKeys.forEach(key => localStorage.removeItem(key));
  }
});
</script>

<style>
/* Poppins: fuentes autohospedadas (public/css/poppins-local.css + preload en index.html) */
@font-face {
    font-family: 'SpiritFox';
    src: url('~@/assets/fonts/Terrasick.ttf') format('truetype');
    font-display: optional;
    font-weight: 400;
    font-style: normal;
    }
* {
margin: 0;
font-family: 'Poppins', sans-serif;
padding: 0;
box-sizing: border-box;
scroll-behavior: smooth;
}
:root {
--black-color: #000000;
--black-color-2: #2b2b2b;
--black-color-3 : #131313;
--grey-color: #d2d2d2bb;
--bg-color: #070707;
--white-color: #ffffff;
--white-color-2: #bdbdbd;
--primary-color: #8300b7;
--primary-color-subtle: #008fb733;
--primary-color-rgb: 0, 140, 37;
--dark-color: #313131;
--light-primary-color: #008fb7;
--dark-primary-color: #008fb7;
--dark-primary-color-2: #007a9f;
--secondary-color: #555555;
--accent-color: #5f28cd;
--primary: #ff4d0033;
--green-color: #00b300;
--tertiary-color: #00bbf9;
}
body, .router-view {
background-color: var(--bg-color);
min-height: 100vh;
}

.font-super{
  font-family: 'SpiritFox', system-ui, sans-serif !important;
  letter-spacing: 2px;
  line-height: 1.08;
  text-rendering: optimizeLegibility;
}
.container{
padding: 0;
max-width: 1500px;
}
.bg-dark{
background-color: var(--black-color-3) !important;
}
.border-primary{
border-color: var(--primary-color) !important;
}	

.display-2, .display-3, .display-4, .display-5, .text-white{
font-weight: 800;
color: var(--white-color-2);
}

section{
min-height: 50vh;
position: relative;
overflow: hidden;
background-color: var(--bg-color);
}

.h5{
font-size: 1.1rem;
font-weight: 500;
color: var(--white-color);
}
.fs-6{
font-size: .8rem;
}
.fs-7{
font-size: .78rem;
}
.btn{
font-size: .8rem;
}

.hover-opacity{
transition: 0.3s ease;
}
.hover-opacity:hover{
opacity: 0.8;
}

/* badges */
.status-badge {
display: inline-flex;
padding: 0.4rem 0.8rem;
border-radius: 2rem;
font-size: 0.875rem;
font-weight: 500;
white-space: nowrap;
}
.btn-outline-primary{
background-color: transparent;
color: var(--primary-color);
border: 1px solid var(--primary-color);
cursor: pointer;
transition: 0.3s ease;
}
.btn-outline-primary.active{
background-color: var(--primary-color);
color: var(--white-color);
border: 1px solid var(--primary-color);
}
.btn-outline-primary:hover{
background-color: var(--primary-color);
color: var(--white-color);
border: 1px solid var(--primary-color);
}
.btn-outline-primary:hover i{
color: var(--white-color);
}
.text-primary{
color: var(--primary-color) !important;
}
.bg-primary{
background-color: var(--primary-color) !important;
}
.text-dark{
color: var(--black-color);
}
.btn{
border-radius: 15px !important;
padding: 0.5rem 1rem;
}
.btn-primary{
background: linear-gradient(90deg, var(--dark-primary-color), var(--primary-color));
color: var(--white-color);
border: none;
border-radius: 15px !important;
cursor: pointer;
transition: 0.3s ease;
}
.btn-primary:hover{
background-color: var(--dark-primary-color) !important;

}
button,a{
font-weight: 400;
font-size: 1.1rem;
}
h1, h2, h3, h4, h5, h6 {
font-weight: 600;
color: var(--white-color);
}
h1 {
font-size: 2rem;
}
h2 {
font-size: 1.7rem;
}
h3 {
font-size: 2rem;
font-weight: 300;
letter-spacing: 1px;
}
h4 {
font-size: 1.5rem;
font-weight: 300;
}
h5 {
font-size: .75rem;
font-weight: 300;
}
p{
font-size: 1rem;
color: var(--white-color-2);
font-weight: 300;
}
b{
font-weight: 700;
}

@media (max-width: 525px) {
  .display-2{
    font-size: 3.5rem !important;
  }
}
@media (max-width: 400px) {
  .display-2{
    font-size: 3rem !important;
  }
}

/*
 * vue-toastification usa SVG con clases tipo Font Awesome; este proyecto carga FA webfont,
 * que no dimensiona esos SVG → quedaban a ~512px. Forzar tamaño acorde al toast.
 */
.Vue-Toastification__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem !important;
  min-width: 1.25rem;
  height: 1.25rem !important;
  max-height: 1.25rem !important;
  overflow: hidden;
}
.Vue-Toastification__icon svg {
  width: 1.25rem !important;
  height: 1.25rem !important;
  display: block;
  vertical-align: middle;
}
</style>
